import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Divider } from "@mui/material";

import { IconNameEnum } from "../../../components/Icon";
import { SliderVariant } from "../../../components/Slider";
import { TextInput } from "../../../components/TextInput";
import {
  BILLING_WEIGHTED_FLAT_FEE_MAX,
  BILLING_WEIGHTED_FLAT_FEE_MAX_DECIMAL_PLACES,
  BILLING_WEIGHTED_FLAT_FEE_MIN,
  BILLING_WEIGHTED_RATE_MAX,
  BILLING_WEIGHTED_RATE_MAX_DECIMAL_PLACES,
  BILLING_WEIGHTED_RATE_MIN,
  BILLING_ZERO_YEAR_AVERAGE_CONTAINER_WEIGHT_DEFAULT,
  BILLING_ZERO_YEAR_AVERAGE_CONTAINER_WEIGHT_MAX,
  BILLING_ZERO_YEAR_AVERAGE_CONTAINER_WEIGHT_MIN,
  BILLING_ZERO_YEAR_PLANNED_COLLECTION_COUNT_DEFAULT,
  BILLING_ZERO_YEAR_PLANNED_COLLECTION_COUNT_MAX,
  BILLING_ZERO_YEAR_PLANNED_COLLECTION_COUNT_MIN,
} from "../../../consts/billing";
import { updateBillingWeightedField } from "../../../reducers/billingReducer";
import {
  getBillingHouseholdWithFixedOnlyCount,
  getBillingIsUpdating,
  getBillingWeighted,
  getIsBillingInNewStatus,
} from "../../../selectors/billing";
import { useAppDispatch } from "../../../store";
import { Weight } from "../../../types/weights";
import { formatNumber } from "../../../utils/number";
import { BillingFeeInput } from "../BillingFeeInput";

import styles from "./BillingZeroYearFeeCalculation.module.scss";

export const BillingZeroYearFeeCalculation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isBillingInNewStatus = useSelector(getIsBillingInNewStatus);
  const isUpdating = useSelector(getBillingIsUpdating);
  const householdWithFixedOnlyCount = useSelector(getBillingHouseholdWithFixedOnlyCount);
  const {
    averageContainerWeightPerCollection,
    plannedAnnualCollectionCount,
    advanceContainerBasis,
    rate,
    suggestedRate,
    flatFee,
    lastYearsWasteSum,
  } = useSelector(getBillingWeighted);

  const isDisabled = isUpdating || !isBillingInNewStatus;

  return (
    <>
      <div className={styles.section}>
        <div className={styles.header}>
          <h5 className={styles.title}>{t("billingWeightedFeeCalculation.flatFeeTitle")}</h5>
          <p className={styles.subtitle}>
            {t("billingWeightedFeeCalculation.householdsInFlatFee")}{" "}
            <span className={styles.highlightedValue}>{householdWithFixedOnlyCount}</span>
          </p>
        </div>
        <BillingFeeInput
          value={flatFee}
          defaultValue={0}
          onChange={(value) => dispatch(updateBillingWeightedField({ field: "flatFee", value }))}
          iconName={IconNameEnum.person}
          sliderVariant={SliderVariant.Orange}
          label={t("billingWeightedFeeCalculation.flatFeeLabel")}
          description={t("billingWeightedFeeCalculation.flatFeeDescription")}
          minValue={BILLING_WEIGHTED_FLAT_FEE_MIN}
          maxValue={BILLING_WEIGHTED_FLAT_FEE_MAX}
          maxDecimalPlaces={BILLING_WEIGHTED_FLAT_FEE_MAX_DECIMAL_PLACES}
          isDisabled={isDisabled}
        />
      </div>
      <div className={styles.section}>
        <h5 className={styles.title}>{t("billingWeightedFeeCalculation.rateTitle")}</h5>
        <div className={styles.inputsRow}>
          <BillingFeeInput
            value={averageContainerWeightPerCollection}
            defaultValue={BILLING_ZERO_YEAR_AVERAGE_CONTAINER_WEIGHT_DEFAULT}
            onChange={(value) =>
              dispatch(updateBillingWeightedField({ field: "averageContainerWeightPerCollection", value }))
            }
            label={t("billingZeroYearFeeCalculation.averageContainerWeight")}
            description={t("billingZeroYearFeeCalculation.averageContainerWeightDescription")}
            minValue={BILLING_ZERO_YEAR_AVERAGE_CONTAINER_WEIGHT_MIN}
            maxValue={BILLING_ZERO_YEAR_AVERAGE_CONTAINER_WEIGHT_MAX}
            maxDecimalPlaces={0}
            isDisabled={isDisabled}
            unit={Weight.Kg}
          />
          <Divider orientation="vertical" flexItem />
          <div>
            <p className={styles.inputLabel}>{t("billingZeroYearFeeCalculation.advanceContainerBasis")}</p>
            <TextInput
              value={formatNumber(advanceContainerBasis)}
              isDisabled
              endItem={<div className={styles.unit}>{t("billingZeroYearFeeCalculation.containersUnit")}</div>}
            />
          </div>
        </div>
        <div className={styles.inputsRow}>
          <BillingFeeInput
            value={rate}
            defaultValue={suggestedRate}
            onChange={(value) => dispatch(updateBillingWeightedField({ field: "rate", value }))}
            label={t("billingZeroYearFeeCalculation.rateLabel")}
            description={t("billingWeightedFeeCalculation.rateDescription")}
            minValue={BILLING_WEIGHTED_RATE_MIN}
            maxValue={BILLING_WEIGHTED_RATE_MAX}
            maxDecimalPlaces={BILLING_WEIGHTED_RATE_MAX_DECIMAL_PLACES}
            isDisabled={isDisabled}
          />
          <Divider orientation="vertical" flexItem />
          <div>
            <p className={styles.inputLabel}>{t("billingZeroYearFeeCalculation.previousPeriodWaste")}</p>
            <TextInput
              value={formatNumber(lastYearsWasteSum)}
              isDisabled
              endItem={<div className={styles.unit}>{Weight.Kg}</div>}
            />
          </div>
        </div>
        <div className={styles.inputsRow}>
          <BillingFeeInput
            value={plannedAnnualCollectionCount}
            defaultValue={BILLING_ZERO_YEAR_PLANNED_COLLECTION_COUNT_DEFAULT}
            onChange={(value) => dispatch(updateBillingWeightedField({ field: "plannedAnnualCollectionCount", value }))}
            iconName={IconNameEnum.car}
            label={t("billingZeroYearFeeCalculation.plannedAnnualCollectionCount")}
            description={t("billingZeroYearFeeCalculation.plannedAnnualCollectionCountDescription")}
            minValue={BILLING_ZERO_YEAR_PLANNED_COLLECTION_COUNT_MIN}
            maxValue={BILLING_ZERO_YEAR_PLANNED_COLLECTION_COUNT_MAX}
            maxDecimalPlaces={0}
            isDisabled={isDisabled}
            unit={t("billingZeroYearFeeCalculation.collectionsUnit")}
          />
          <Divider orientation="vertical" flexItem />
          <div aria-hidden="true" />
        </div>
      </div>
    </>
  );
};
